// unit test for this file
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/dist/query';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useAppSelector } from '../../../app/hooks';
import {
  useAddContactMutation,
  useEditContactMutation,
  useGetContactInfoQuery,
  useGetContactRolesQuery,
} from '../../../app/services/account';
import { useToast } from '../../../app/toast';
import Loading from '../../../common/components/Loading';
import PageHeader from '../../../common/components/PageHeader';
import DashboardLayout from '../../../common/layouts/Dashboard';
import { editBreadcrumbs, ReplaceType } from '../../../common/utils/editBreadcrums';
import { getPageTitle } from '../../../common/utils/pageUtils';
import { isClinicAdmin } from '../../../common/utils/permissionUtils';
import { ContactRequest } from '../../../types/ContactType';
import { PageProps } from '../../../types/PageProps';
import { selectUser } from '../../auth/authSlice';
import ErrorPage from '../../error/ErrorPage';
import ContactForm from './ContactForm';

export default function AddOrEditContactPage({ breadcrumbs }: PageProps) {
  const { t } = useTranslation();
  const params = useParams();
  const contactId: string | undefined = params.contactId;
  const clinicalAccountId: string | undefined = params.clinicalAccountId;
  const navigate = useNavigate();
  const toast = useToast();
  const currentUser = useAppSelector(selectUser);

  const [addContact] = useAddContactMutation();
  const [editContact] = useEditContactMutation();

  const {
    data: contactInfoData,
    error,
    isLoading: loadContactData,
    refetch: refetchEditContact,
  } = useGetContactInfoQuery(!!contactId ? { contactId: parseInt(contactId) } : skipToken);

  const { data: contactRoleData, isLoading: loadContactRolesData } =
    useGetContactRolesQuery(undefined);

  const { state } = useLocation();
  const { navBackUrl } = !!state && state;
  const replacePath: ReplaceType[] = [{ param: ':clinicalAccountId', value: clinicalAccountId! }];

  const onFormSubmit = async (data: ContactRequest, successCallback?: () => void) => {
    const fullData = { ...data, clinicAccount: clinicalAccountId };
    if (contactId) {
      await editContact({ ...fullData }).unwrap();
      refetchEditContact();
    } else {
      await addContact(fullData).unwrap();
      navigate(`/dashboard/accounts/${clinicalAccountId}/contacts`);
    }

    if (successCallback) {
      successCallback();
    }

    toast.publish(
      (contactId && t('account.contacts.manageContacts.editSuccess')) ||
        t('account.contacts.manageContacts.addSuccess'),
      'success',
      5000
    );
  };

  if (error) {
    return <ErrorPage statusCode={(error as FetchBaseQueryError).status} />;
  }
  if (breadcrumbs?.length && isClinicAdmin(currentUser.roleId)) {
    breadcrumbs = [breadcrumbs[0], breadcrumbs[2], breadcrumbs[3]];
  }

  return (
    <DashboardLayout breadcrumbs={editBreadcrumbs(breadcrumbs, ...replacePath)}>
      <Helmet>
        <title>
          {contactId
            ? getPageTitle(t('account.contacts.editContact'))
            : getPageTitle(t('account.contacts.addContact'))}
        </title>
      </Helmet>

      <Container maxWidth="xl">
        <PageHeader
          sx={{ mt: 1, mb: 2 }}
          headerText={
            contactId ? t('account.contacts.editContact') : t('account.contacts.addContact')
          }
          backUrl={navBackUrl || `/dashboard/accounts/${clinicalAccountId}/contacts`}
        />
      </Container>

      {loadContactData || loadContactRolesData ? (
        <Box sx={{ my: 2 }}>
          <Loading />
        </Box>
      ) : (
        <ContactForm
          data={contactInfoData?.contact}
          roles={contactRoleData?.roles}
          onSubmit={onFormSubmit}
        />
      )}
    </DashboardLayout>
  );
}
